/**
 * Partition-then-stitch watertight wall extrusion.
 *
 * Input: a planar PARTITION of the wall footprint into per-element regions —
 * each wall edge is its own quad, each junction node its own n-gon — that TILE
 * (adjacent regions share exact boundary vertices). Each region carries its own
 * `[bottom, top]` world-Z range.
 *
 * Output: ONE connected, welded, watertight 2-manifold triangle mesh
 * (`{ positions, indices }`) where:
 *   - each region contributes a bottom cap (at `bottom`) and a top cap (at `top`);
 *   - exterior boundary segments (touched by one region) get a full-height wall;
 *   - segments shared by two regions of DIFFERENT height get a vertical "step"
 *     wall spanning the symmetric difference of their Z ranges;
 *   - segments shared by equal-height regions get nothing (interior, hidden).
 *
 * The single welded 3D vertex table + subdividing every vertical face at the
 * union of its endpoints' incident Z-levels eliminates duplicate vertices and
 * T-junctions, so the result is a valid 2-manifold — Manifold CSG accepts it for
 * the door/window subtraction. This is viewer-independent and unit-testable.
 */

import * as THREE from 'three';
import earcut from 'earcut';

export interface WallRegion {
  /** Closed ring (no repeated last point), CCW or CW — winding is normalised here. */
  ring: THREE.Vector2[];
  bottom: number;
  top: number;
  /**
   * Optional convex sub-polygons to CARVE OUT of `ring` (the node kernel cells a
   * wall rectangle overlaps at each end). Every edge of `ring` and of each
   * subtract cell lies on a real wall offset line, so `ring − subtract` has NO
   * off-line/diagonal edges. Honoured by the CSG path (`subtractFromRegions`);
   * the flat-display stitcher ignores it (the brief pre-WASM overlap is hidden).
   */
  subtract?: THREE.Vector2[][];
  /** True for a node junction kernel cell (vs. an edge wall rectangle). */
  isNodeCell?: boolean;
}

export interface StitchedMesh {
  positions: number[];
  indices: number[];
}

/** 2D weld key (0.1 mm grid). */
const k2 = (x: number, y: number) => `${x.toFixed(5)},${y.toFixed(5)}`;
/** 3D weld key (0.1 mm grid). */
const k3 = (x: number, y: number, z: number) => `${x.toFixed(5)},${y.toFixed(5)},${z.toFixed(5)}`;
/** Quantise a Z level so "equal" heights compare identical. */
const qz = (z: number) => Math.round(z / 1e-5) * 1e-5;

const signedArea = (ring: THREE.Vector2[]): number => {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
};

export function buildStitchedWallMesh(regions: WallRegion[]): StitchedMesh {
  // --- 2D weld table: every corner → a stable 2D id ---
  const idOf = new Map<string, number>();
  const xy: { x: number; y: number }[] = [];
  const weld2 = (p: THREE.Vector2): number => {
    const key = k2(p.x, p.y);
    let id = idOf.get(key);
    if (id === undefined) {
      id = xy.length;
      idOf.set(key, id);
      xy.push({ x: p.x, y: p.y });
    }
    return id;
  };

  // Normalise each region ring to CCW (positive area) and weld its corners.
  type R = { ring2d: number[]; bottom: number; top: number };
  const rs: R[] = [];
  for (const region of regions) {
    if (region.ring.length < 3) continue;
    const ring = signedArea(region.ring) >= 0 ? region.ring : [...region.ring].reverse();
    const ring2d = ring.map(weld2);
    // Drop consecutive duplicates that welding may have produced.
    const dedup: number[] = [];
    for (let i = 0; i < ring2d.length; i++) {
      if (ring2d[i] !== ring2d[(i + 1) % ring2d.length]) dedup.push(ring2d[i]);
    }
    if (dedup.length < 3) continue;
    rs.push({ ring2d: dedup, bottom: qz(region.bottom), top: qz(region.top) });
  }
  if (rs.length === 0) return { positions: [], indices: [] };

  // --- Boundary-segment map + GLOBAL Z-level set ---
  // Every region bottom/top is a global cut level: all vertical faces are
  // subdivided at every level inside their span. Subdividing only where a
  // face's own endpoints touch a stepped neighbour (the previous per-vertex
  // scheme) split a wall at one end but not the faces meeting its OTHER end,
  // leaving T-junctions there (non-manifold edges on step walls).
  const segKey = (a: number, b: number) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const segments = new Map<string, { v0: number; v1: number; touch: { bottom: number; top: number; fwd: boolean }[] }>();
  const zLevelSet = new Set<number>();

  for (const r of rs) {
    zLevelSet.add(r.bottom);
    zLevelSet.add(r.top);
    for (let i = 0; i < r.ring2d.length; i++) {
      const a = r.ring2d[i];
      const b = r.ring2d[(i + 1) % r.ring2d.length];
      const key = segKey(a, b);
      let seg = segments.get(key);
      if (!seg) {
        seg = { v0: a, v1: b, touch: [] };
        segments.set(key, seg);
      }
      // fwd = this region traversed the segment in v0→v1 order (its CCW dir).
      seg.touch.push({ bottom: r.bottom, top: r.top, fwd: a === seg.v0 });
    }
  }

  // --- Welded 3D vertex table + emission ---
  const positions: number[] = [];
  const indices: number[] = [];
  const idx3 = new Map<string, number>();
  const weld3 = (x: number, y: number, z: number): number => {
    const key = k3(x, y, z);
    let id = idx3.get(key);
    if (id === undefined) {
      id = positions.length / 3;
      idx3.set(key, id);
      positions.push(x, y, z);
    }
    return id;
  };
  const tri = (a: number, b: number, c: number) => indices.push(a, b, c);

  // Caps: bottom wound CW-from-above (down normal), top CCW-from-above (up normal).
  // We orient EACH earcut triangle by its own signed area rather than trusting the
  // ring's winding — earcut can emit a triangle wound against a non-convex or
  // near-degenerate ring, and one inconsistently-wound cap triangle makes the
  // whole mesh non-orientable (Manifold rejects it). Forcing CCW-in-XY per
  // triangle, then reversing for the bottom, guarantees globally consistent caps.
  for (const r of rs) {
    const flat: number[] = [];
    for (const v of r.ring2d) flat.push(xy[v].x, xy[v].y);
    const t = earcut(flat, null, 2);
    for (let i = 0; i < t.length; i += 3) {
      const A = xy[r.ring2d[t[i]]];
      let B = xy[r.ring2d[t[i + 1]]];
      let C = xy[r.ring2d[t[i + 2]]];
      const signed = (B.x - A.x) * (C.y - A.y) - (C.x - A.x) * (B.y - A.y);
      if (signed === 0) continue; // sliver — drop (Manifold ignores zero-area tris)
      if (signed < 0) {
        const tmp = B;
        B = C;
        C = tmp;
      }
      // bottom faces −Z (reverse of CCW), top faces +Z (CCW).
      tri(weld3(A.x, A.y, r.bottom), weld3(C.x, C.y, r.bottom), weld3(B.x, B.y, r.bottom));
      tri(weld3(A.x, A.y, r.top), weld3(B.x, B.y, r.top), weld3(C.x, C.y, r.top));
    }
  }

  // Vertical faces per boundary segment.
  const zLevels = Array.from(zLevelSet).sort((a, b) => a - b);
  const levelsIn = (lo: number, hi: number): number[] => [lo, ...zLevels.filter((z) => z > lo + 1e-7 && z < hi - 1e-7), hi];

  // Emit one vertical wall along segment (p0→p1) over [lo,hi], normal pointing
  // OUTWARD as defined by the 2D direction p0→p1 (outward = +90° of travel for a
  // CCW-wound filled region on the left). We subdivide at every global Z-level
  // inside the span so ALL faces cut at identical heights and neighbouring
  // faces/caps weld with no T-junction anywhere.
  const emitWall = (p0: number, p1: number, lo: number, hi: number, flip: boolean) => {
    if (hi - lo < 1e-6) return;
    const A = xy[p0];
    const B = xy[p1];
    const cut = levelsIn(lo, hi);
    for (let i = 0; i < cut.length - 1; i++) {
      const za = cut[i];
      const zb = cut[i + 1];
      const a0 = weld3(A.x, A.y, za);
      const b0 = weld3(B.x, B.y, za);
      const b1 = weld3(B.x, B.y, zb);
      const a1 = weld3(A.x, A.y, zb);
      if (!flip) {
        tri(a0, b0, b1);
        tri(a0, b1, a1);
      } else {
        tri(a0, b1, b0);
        tri(a0, a1, b1);
      }
    }
  };

  for (const seg of segments.values()) {
    const { v0, v1, touch } = seg;
    if (touch.length === 1) {
      // Exterior wall — full height. The single region wound CCW puts its fill on
      // the left of v0→v1, so the outward normal is to the right: emit unflipped.
      emitWall(v0, v1, touch[0].bottom, touch[0].top, false);
    } else if (touch.length === 2) {
      const [r0, r1] = touch;
      // Top step: the SHORTER region (smaller top) closes its top cap at z=tlo.
      // That cap edge traverses this segment in the region's own CCW direction
      // (its `fwd`). The step wall's lower edge at tlo must run OPPOSITE that cap
      // edge to be manifold, so flip = shorter.fwd (a face that ignores the
      // region's traversal direction breaks when neighbour tops differ).
      const tlo = Math.min(r0.top, r1.top);
      const thi = Math.max(r0.top, r1.top);
      if (thi - tlo > 1e-6) {
        const shorter = r0.top <= r1.top ? r0 : r1;
        emitWall(v0, v1, tlo, thi, shorter.fwd);
      }
      // Bottom step: the region with the HIGHER bottom closes its bottom cap at
      // z=bhi. The bottom cap is emitted REVERSED (down normal), so it traverses
      // the segment opposite the region's CCW dir (the !fwd direction); the step
      // wall's upper edge must oppose that, so flip = !higher.fwd.
      const blo = Math.min(r0.bottom, r1.bottom);
      const bhi = Math.max(r0.bottom, r1.bottom);
      if (bhi - blo > 1e-6) {
        const higher = r0.bottom >= r1.bottom ? r0 : r1;
        emitWall(v0, v1, blo, bhi, !higher.fwd);
      }
    }
    // touch.length > 2 → invalid tiling; skip (a clean partition never hits this).
  }

  return { positions, indices };
}

/**
 * Diagnostic: list the edges of an indexed triangle mesh that are NOT shared by
 * exactly two triangles (i.e. the reasons it isn't a closed 2-manifold). Welds
 * coincident positions first. Returns each bad edge's two endpoints + how many
 * triangles use it. Used to pinpoint where the stitched base breaks.
 */
export function nonManifoldEdges(positions: number[], indices: number[]): Array<{ a: [number, number, number]; b: [number, number, number]; count: number }> {
  const idOf = new Map<string, number>();
  const pos: [number, number, number][] = [];
  const id = (i: number): number => {
    const x = positions[i * 3];
    const y = positions[i * 3 + 1];
    const z = positions[i * 3 + 2];
    const k = `${x.toFixed(5)},${y.toFixed(5)},${z.toFixed(5)}`;
    let n = idOf.get(k);
    if (n === undefined) {
      n = pos.length;
      idOf.set(k, n);
      pos.push([x, y, z]);
    }
    return n;
  };
  const edge = new Map<string, number>();
  for (let t = 0; t < indices.length; t += 3) {
    const a = id(indices[t]);
    const b = id(indices[t + 1]);
    const c = id(indices[t + 2]);
    for (const [p, q] of [
      [a, b],
      [b, c],
      [c, a],
    ]) {
      const key = p < q ? `${p}|${q}` : `${q}|${p}`;
      edge.set(key, (edge.get(key) ?? 0) + 1);
    }
  }
  const out: Array<{ a: [number, number, number]; b: [number, number, number]; count: number }> = [];
  for (const [key, count] of edge) {
    if (count === 2) continue;
    const [p, q] = key.split('|').map(Number);
    out.push({ a: pos[p], b: pos[q], count });
  }
  return out;
}

export interface Seg2 {
  a: THREE.Vector2;
  b: THREE.Vector2;
}

/**
 * Convex hull (CCW) of a small 2D point set via Andrew's monotone chain.
 * Returns the hull as an open ring (no repeated last point). For ≤ 2 distinct
 * points returns them as-is. Used to order a corner cell's offset-line
 * intersections into a simple (non-self-intersecting) quad.
 */
export function convexHull2D(points: THREE.Vector2[]): THREE.Vector2[] {
  const pts = points
    .map((p) => ({ x: p.x, y: p.y }))
    .filter((p, i, arr) => arr.findIndex((q) => Math.abs(q.x - p.x) < 1e-7 && Math.abs(q.y - p.y) < 1e-7) === i)
    .sort((p, q) => (p.x === q.x ? p.y - q.y : p.x - q.x));
  if (pts.length < 3) return pts.map((p) => new THREE.Vector2(p.x, p.y));

  const cross = (o: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: { x: number; y: number }[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: { x: number; y: number }[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper].map((p) => new THREE.Vector2(p.x, p.y));
}

/**
 * Merge near-collinear, overlapping/touching segments into one shared segment.
 * Two segments merge when their directions are nearly parallel, they lie on
 * (nearly) the same infinite line (small perpendicular offset), and their
 * projections onto that line overlap or touch within `gapTol`. Each merged
 * cluster is replaced by the single segment spanning its extent along the line.
 */
export function mergeCollinearSegments(segments: Seg2[], angleTol = 0.02, perpTol = 0.02, gapTol = 0.01): Seg2[] {
  const n = segments.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const union = (i: number, j: number) => {
    parent[find(i)] = find(j);
  };

  const dir = (s: Seg2) => new THREE.Vector2().subVectors(s.b, s.a).normalize();
  const len = (s: Seg2) => s.a.distanceTo(s.b);

  for (let i = 0; i < n; i++) {
    if (len(segments[i]) < 1e-9) continue;
    const di = dir(segments[i]);
    for (let j = i + 1; j < n; j++) {
      if (len(segments[j]) < 1e-9) continue;
      const dj = dir(segments[j]);
      if (Math.abs(di.dot(dj)) < 1 - angleTol) continue; // not parallel
      // Perpendicular offset of j's endpoints from i's line.
      const perp = new THREE.Vector2(-di.y, di.x);
      const o0 = Math.abs(perp.dot(new THREE.Vector2().subVectors(segments[j].a, segments[i].a)));
      const o1 = Math.abs(perp.dot(new THREE.Vector2().subVectors(segments[j].b, segments[i].a)));
      if (o0 > perpTol || o1 > perpTol) continue; // not on the same line
      // Projections onto i's direction; merge if they overlap/touch.
      const ti0 = 0;
      const ti1 = di.dot(new THREE.Vector2().subVectors(segments[i].b, segments[i].a));
      const tj0 = di.dot(new THREE.Vector2().subVectors(segments[j].a, segments[i].a));
      const tj1 = di.dot(new THREE.Vector2().subVectors(segments[j].b, segments[i].a));
      const [iLo, iHi] = [Math.min(ti0, ti1), Math.max(ti0, ti1)];
      const [jLo, jHi] = [Math.min(tj0, tj1), Math.max(tj0, tj1)];
      if (jLo > iHi + gapTol || iLo > jHi + gapTol) continue; // disjoint
      union(i, j);
    }
  }

  // Collect clusters; emit one spanning segment each.
  const clusters = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    const arr = clusters.get(r) ?? [];
    arr.push(i);
    clusters.set(r, arr);
  }

  const out: Seg2[] = [];
  for (const idxs of clusters.values()) {
    if (idxs.length === 1) {
      out.push(segments[idxs[0]]);
      continue;
    }
    // Direction + anchor from the longest member (most stable).
    let lead = idxs[0];
    for (const k of idxs) if (len(segments[k]) > len(segments[lead])) lead = k;
    const d = dir(segments[lead]);
    const anchor = segments[lead].a;
    let lo = Infinity;
    let hi = -Infinity;
    for (const k of idxs) {
      for (const p of [segments[k].a, segments[k].b]) {
        const t = d.dot(new THREE.Vector2().subVectors(p, anchor));
        if (t < lo) lo = t;
        if (t > hi) hi = t;
      }
    }
    out.push({ a: anchor.clone().add(d.clone().multiplyScalar(lo)), b: anchor.clone().add(d.clone().multiplyScalar(hi)) });
  }
  return out;
}

/**
 * Deduped 2D boundary segments of the partition (for the debug overlay). Each
 * segment is a wall region ring edge — already bounded to its wall by the
 * cap-based construction — so we only drop EXACT duplicates (the shared boundary
 * two adjacent regions both emit). We deliberately do NOT span/merge collinear
 * segments: that would extend a line past its wall. Use mergeCollinearSegments
 * explicitly where a single merged line is genuinely wanted.
 */
export function partitionSegments(regions: WallRegion[]): Seg2[] {
  const seen = new Set<string>();
  const raw: Seg2[] = [];
  for (const region of regions) {
    const ring = region.ring;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      const ka = k2(a.x, a.y);
      const kb = k2(b.x, b.y);
      const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
      if (seen.has(key)) continue;
      seen.add(key);
      raw.push({ a: a.clone(), b: b.clone() });
    }
  }
  return raw;
}

/**
 * The EXTERIOR boundary of the wall partition: ring segments touched by exactly
 * ONE region. These trace the wall silhouette (outer outline + interior cavity
 * loops) with no internal cell-division lines, so they make the clean outline
 * for the flat 2D skinning — the same footprint the 3D extrusion uses, minus the
 * partition seams. (Interior seams between two regions appear twice and drop.)
 */
export function partitionExteriorSegments(regions: WallRegion[]): Seg2[] {
  const count = new Map<string, number>();
  const sample = new Map<string, Seg2>();
  for (const region of regions) {
    const ring = region.ring;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      const ka = k2(a.x, a.y);
      const kb = k2(b.x, b.y);
      if (ka === kb) continue;
      const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
      count.set(key, (count.get(key) ?? 0) + 1);
      if (!sample.has(key)) sample.set(key, { a: a.clone(), b: b.clone() });
    }
  }
  const out: Seg2[] = [];
  for (const [key, n] of count) {
    if (n !== 1) continue;
    const seg = sample.get(key);
    if (seg) out.push(seg);
  }
  return out;
}
